/**
 * Global Partner Protection Module
 * This module intercepts ALL logout attempts and prevents them for partners
 */

// Store original localStorage methods
const originalRemoveItem = localStorage.removeItem.bind(localStorage);
const originalClear = localStorage.clear.bind(localStorage);

// Flag to allow explicit logout (bypass protection)
let allowExplicitLogout = false;

// Function to enable explicit logout (call this before logging out)
export const enableExplicitLogout = () => {
  allowExplicitLogout = true;
  // Also set in sessionStorage as backup
  try {
    sessionStorage.setItem('_allowExplicitLogout', 'true');
  } catch (e) {
    // Ignore
  }
  console.log('🔓 Partner Protection: Explicit logout enabled');
};

// Make it available globally for easier access
if (typeof window !== 'undefined') {
  window._enableExplicitLogout = enableExplicitLogout;
}

// Check if explicit logout is allowed (check both flag and sessionStorage)
const isExplicitLogoutEnabled = () => {
  if (allowExplicitLogout) return true;
  try {
    return sessionStorage.getItem('_allowExplicitLogout') === 'true';
  } catch (e) {
    return false;
  }
};

// Function to check if current user is a partner
const isCurrentUserPartner = () => {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) return false;

    const user = JSON.parse(userStr);
    return user.role === 'client' || user.userType === 'partner';
  } catch (error) {
    return false;
  }
};

// Override localStorage.removeItem to protect partner tokens
localStorage.removeItem = function (key) {
  // If removing user-related items and current user is a partner, prevent it
  // UNLESS explicit logout is enabled
  // if (['accessToken', 'refreshToken', 'user', 'userInfo'].includes(key)) {
  //   if (isCurrentUserPartner() && !isExplicitLogoutEnabled()) {
  //     console.log('🔒 GLOBAL PROTECTION: Preventing removal of', key, 'for partner user');
  //     alert('🔒 PARTNER PROTECTION: Token removal blocked! You will stay logged in.');
  //     return; // Do nothing
  //   }
  // }

  // For non-partners or other keys, proceed normally
  // Or if explicit logout is enabled, proceed normally
  originalRemoveItem(key);

  // Reset flag after use
  if (allowExplicitLogout) {
    allowExplicitLogout = false;
  }
};

// Override localStorage.clear to protect partner data
localStorage.clear = function () {
  // if (isCurrentUserPartner() && !isExplicitLogoutEnabled()) {
  //   console.log('🔒 GLOBAL PROTECTION: Preventing localStorage.clear() for partner user');
  //   alert('🔒 PARTNER PROTECTION: localStorage clear blocked! You will stay logged in.');
  //   return; // Do nothing
  // }

  // For non-partners, proceed normally
  // Or if explicit logout is enabled, proceed normally
  originalClear();

  // Reset flag after use
  if (allowExplicitLogout) {
    allowExplicitLogout = false;
  }
};

// Create a global function for safe navigation
window._safeNavigate = function (url) {
  if (typeof url === 'string' && url.includes('login')) {
    // if (isCurrentUserPartner() && !isExplicitLogoutEnabled()) {
    //   console.log('🔒 GLOBAL PROTECTION: Preventing navigation to login for partner user:', url);
    //   alert('🔒 PARTNER PROTECTION: Redirect to login blocked! You will stay logged in.');
    //   return; // Do nothing
    // }
  }

  // For non-partners or non-login URLs, proceed normally
  window.location.href = url;
};

// AGGRESSIVE REDIRECT PROTECTION
// Override window.location.href with a more aggressive approach
let _isRedirecting = false;

// Store the original location object
const originalLocation = window.location;

// Create a custom location object that intercepts href assignments
const protectedLocation = {
  get href() {
    return originalLocation.href;
  },
  set href(value) {
    console.log('🔍 GLOBAL PROTECTION: href assignment detected:', value);

    // Log stack trace to see what's causing the redirect
    if (typeof value === 'string' && value.includes('login')) {
      console.log('🔍 REDIRECT SOURCE TRACE for login redirect:');
      console.trace('Stack trace for redirect to:', value);
    }

    if (typeof value === 'string' && value.includes('login')) {
      // if (isCurrentUserPartner() && !isExplicitLogoutEnabled()) {
      //   console.log('🔒 GLOBAL PROTECTION: BLOCKING redirect to login for partner user:', value);
      //   alert('🔒 PARTNER PROTECTION: Redirect to login blocked! You will stay logged in.');
      //   return; // Do nothing - block the redirect
      // }
    }

    // For non-partners or non-login URLs, proceed normally
    try {
      originalLocation.href = value;
    } catch (error) {
      console.error('Error setting location.href:', error);
    }
  },
};

// Try to replace window.location with our protected version
try {
  Object.defineProperty(window, 'location', {
    value: protectedLocation,
    writable: false,
    configurable: false,
  });
  console.log('✅ Successfully protected window.location');
} catch (error) {
  console.warn('Could not protect window.location directly:', error);

  // Fallback: Try to override individual methods
  try {
    const originalAssign = window.location.assign;
    const originalReplace = window.location.replace;

    window.location.assign = function (url) {
      console.log('🔍 GLOBAL PROTECTION: assign() called with:', url);

      // if (typeof url === 'string' && url.includes('login')) {
      //   if (isCurrentUserPartner() && !isExplicitLogoutEnabled()) {
      //     console.log('🔒 GLOBAL PROTECTION: BLOCKING assign to login for partner user:', url);
      //     alert('🔒 PARTNER PROTECTION: Assign to login blocked! You will stay logged in.');
      //     return; // Do nothing
      //   }
      // }

      originalAssign.call(this, url);
    };

    window.location.replace = function (url) {
      console.log('🔍 GLOBAL PROTECTION: replace() called with:', url);

      // if (typeof url === 'string' && url.includes('login')) {
      //   if (isCurrentUserPartner() && !isExplicitLogoutEnabled()) {
      //     console.log('🔒 GLOBAL PROTECTION: BLOCKING replace to login for partner user:', url);
      //     alert('🔒 PARTNER PROTECTION: Replace to login blocked! You will stay logged in.');
      //     return; // Do nothing
      //   }
      // }

      originalReplace.call(this, url);
    };

    console.log('✅ Successfully protected location methods as fallback');
  } catch (fallbackError) {
    console.warn('Could not protect location methods:', fallbackError);
  }
}

// Additional protection: Intercept any direct window.location assignments
const originalWindowLocation = window.location;

// Override window.location if possible
try {
  Object.defineProperty(window, 'location', {
    get: function () {
      return protectedLocation;
    },
    set: function (value) {
      console.log('🔍 GLOBAL PROTECTION: window.location assignment detected:', value);

      if (typeof value === 'string' && value.includes('login')) {
        // if (isCurrentUserPartner() && !isExplicitLogoutEnabled()) {
        //   console.log(
        //     '🔒 GLOBAL PROTECTION: BLOCKING window.location assignment to login for partner user:',
        //     value
        //   );
        //   alert(
        //     '🔒 PARTNER PROTECTION: Window location assignment to login blocked! You will stay logged in.'
        //   );
        //   return; // Do nothing
        // }
      }

      // For non-partners or non-login URLs, proceed normally
      try {
        originalWindowLocation.href = value;
      } catch (error) {
        console.error('Error setting window.location:', error);
      }
    },
    configurable: true,
  });
} catch (error) {
  console.warn('Could not override window.location:', error);
}

// Additional protection: Global event listener for navigation
window.addEventListener('beforeunload', function (event) {
  if (isCurrentUserPartner()) {
    console.log('🔍 GLOBAL PROTECTION: beforeunload event detected for partner user');
    // Don't prevent the event, just log it
  }
});

// Monitor for any navigation changes
let currentUrl = window.location.href;
let redirectCount = 0;
const maxRedirects = 3;
let isRedirecting = false;
let redirectProtectionDisabled = false;

// const urlMonitor = setInterval(() => {
//   // Stop monitoring if protection is disabled due to too many redirects
//   if (redirectProtectionDisabled) {
//     clearInterval(urlMonitor);
//     return;
//   }

//   const newUrl = window.location.href;
//   if (newUrl !== currentUrl && !isRedirecting) {
//     redirectCount++;
//     console.log(
//       `🔍 GLOBAL PROTECTION: URL change detected (${redirectCount}/${maxRedirects}):`,
//       currentUrl,
//       '->',
//       newUrl
//     );

//     // Prevent infinite redirect loops
//     if (redirectCount > maxRedirects) {
//       console.log(
//         '🚨 GLOBAL PROTECTION: Too many redirects detected! Disabling redirect protection to prevent infinite loop.'
//       );
//       redirectProtectionDisabled = true;
//       clearInterval(urlMonitor);
//       return;
//     }

//     // Reset redirect count if we reach a stable app route (not login)
//     if (!newUrl.includes('login')) {
//       console.log('✅ GLOBAL PROTECTION: Reached stable route, resetting redirect count');
//       redirectCount = 0;
//       currentUrl = newUrl;
//       return;
//     }

//     // Only intervene if partner is being redirected to login AND it's NOT from admin login page
//     // Allow admin/login page to handle its own redirects
//     // AND explicit logout is not enabled
//     if (
//       newUrl.includes('login') &&
//       isCurrentUserPartner() &&
//       !currentUrl.includes('/login') &&
//       !isExplicitLogoutEnabled()
//     ) {
//       console.log(
//         '🔒 GLOBAL PROTECTION: Partner redirected to login from non-admin page! Attempting to redirect back...'
//       );
//       console.log('🔍 GLOBAL PROTECTION: Current URL:', currentUrl);
//       console.log('🔍 GLOBAL PROTECTION: Target URL:', newUrl);

//       // Mark as redirecting to prevent multiple simultaneous redirects
//       isRedirecting = true;

//       // Try to redirect back to the partner space
//       setTimeout(() => {
//         console.log('🔒 GLOBAL PROTECTION: Executing redirect back to partner space');
//         try {
//           window.location.replace('/');
//         } catch (error) {
//           console.error('Error redirecting to partner space:', error);
//         } finally {
//           isRedirecting = false;
//         }
//       }, 500);
//     } else if (currentUrl.includes('/login') && newUrl.includes('/')) {
//       // If redirecting from /login to /, and user is a partner, redirect to partner space
//       // BUT NOT if explicit logout is enabled
//       if (isCurrentUserPartner() && !isExplicitLogoutEnabled()) {
//         console.log(
//           '🔒 GLOBAL PROTECTION: Partner redirected to /, intercepting and redirecting to partner space'
//         );
//         isRedirecting = true;
//         setTimeout(() => {
//           try {
//             window.location.replace('/');
//           } catch (error) {
//             console.error('Error redirecting to partner space:', error);
//           } finally {
//             isRedirecting = false;
//           }
//         }, 100);
//       }
//     }

//     currentUrl = newUrl;
//   }
// }, 100); // Check every 100ms


export default {
  isCurrentUserPartner,
  enableExplicitLogout,
};
